var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import styles from './video-list.styles';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '../button/button';
import { dictionary } from '@mf-cms/helpers';
let PVideoList = class PVideoList extends LitElement {
    constructor() {
        super(...arguments);
        this.type = 'grid'; // "grid" | "list"
        this.heading = '';
        this.sortOrder = 'asc';
        this.showAllLink = '';
        this.videoBaseUrl = '';
        this.liveBaseUrl = '';
        this.culture = 'en-GB';
        this.showRuntime = false;
        this.videos = [];
        this.liveVideos = [];
        this.initialId = 0; //set inital folder id, needed for change in the admin
        this.initialSort = 'name';
        this.initialSortOrder = 'asc';
        this.initialLimit = 10;
        this.showLeftSliderNav = false;
        this.showRightSliderNav = true;
        this.gridItemWidthFirstCheck = true;
        this.gridItemWidth = 200;
    }
    static { this.styles = styles; }
    /**
     * Connected callback lifecycle
     * @returns {void}
     */
    connectedCallback() {
        super.connectedCallback();
    }
    /**
     * First updated lifecycle
     * @returns {void}
     */
    async firstUpdated() {
        this.accessToken = window.getAccessToken();
        if (this.folderId) {
            this.initialId = parseInt(this.folderId);
            this.initialSort = this.sort;
            this.initialSortOrder = this.sortOrder;
            this.videos = await this.getVideos(parseInt(this.folderId));
            this.liveVideos = await this.getLiveVideos(this.folderId);
            // start live ticker for live videos, update every 10 seconds
            this.interval = setInterval(async () => {
                this.liveVideos = await this.getLiveVideos(this.folderId);
            }, 10000);
            this.requestUpdate();
        }
        let gridItemDefaultWidth = getComputedStyle(document.documentElement).getPropertyValue('--p-video-item-width');
        if (gridItemDefaultWidth) {
            gridItemDefaultWidth = parseInt(gridItemDefaultWidth);
            this.gridItemWidth = gridItemDefaultWidth;
        }
        this.resizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (entry?.contentRect?.width) {
                    if (this.gridItemWidth == gridItemDefaultWidth &&
                        this.gridItemWidthFirstCheck &&
                        parseInt(entry?.contentRect?.width) !== gridItemDefaultWidth &&
                        this.videos &&
                        this.videos.length !== 0) {
                        this.gridItemWidthFirstCheck = false;
                        this.sideScrollTo(0, false);
                    }
                    this.gridItemWidth = parseInt(entry?.contentRect?.width);
                    this.checkScroll();
                }
            });
        });
        if (this.ghostGridItemEl) {
            this.resizeObserver.observe(this.ghostGridItemEl);
        }
    }
    async updated() {
        // get videos if folderId is set to a new value
        if ((this.folderId && this.initialId !== parseInt(this.folderId)) ||
            (this.limit && this.initialLimit !== parseInt(this.limit))) {
            this.initialId = parseInt(this.folderId);
            this.initialLimit = parseInt(this.limit);
            this.videos = await this.getVideos(parseInt(this.folderId));
            this.liveVideos = await this.getLiveVideos(this.folderId);
            // start live ticker for live videos, update every 10 seconds
            this.interval = setInterval(async () => {
                this.liveVideos = await this.getLiveVideos(this.folderId);
            }, 10000);
        }
        // get videos if sort is set to a new value
        if ((this.sort && this.initialSort !== this.sort) ||
            (this.sortOrder && this.initialSortOrder !== this.sortOrder)) {
            this.initialSort = this.sort;
            this.initialSortOrder = this.sortOrder;
            this.videos = await this.getVideos(parseInt(this.folderId));
            this.liveVideos = await this.getLiveVideos(this.folderId);
        }
        // clear videos if folderId is removed
        if (!this.folderId && (this.videos.length > 0 || this.liveVideos.length > 0)) {
            this.videos = [];
            this.liveVideos = [];
        }
        this.checkScroll();
    }
    async getVideos(folderId) {
        if (folderId == 0) {
            try {
                const response = await fetch(`${process.env.API_URL}/files/latest?fields=any&type=video&access_token=${this.accessToken}&page=1&per_page=${this.limit}&sort=${this.sort}&order=${this.sortOrder}`);
                if (response.status === 404) {
                    return [];
                }
                const json = await response.json();
                return json;
            }
            catch (e) {
                console.error(e);
            }
            return [];
        }
        try {
            const response = await fetch(`${process.env.API_URL}/folder/${folderId}/files?fields=any&type=video&access_token=${this.accessToken}&page=1&per_page=${this.limit}&sort=${this.sort}&order=${this.sortOrder}`);
            if (response.status === 404) {
                return [];
            }
            const json = await response.json();
            return json;
        }
        catch (e) {
            console.error(e);
        }
        return [];
    }
    async getLiveVideos(folderId) {
        if (folderId == 0) {
            return [];
        }
        try {
            const response = await fetch(`${process.env.API_URL}/folder/${folderId}/livestreams?fields=any&access_token=${this.accessToken}`);
            const json = await response.json();
            if (response.status === 404) {
                return [];
            }
            return json;
        }
        catch (e) {
            console.error(e);
        }
        return [];
    }
    /**
     * Disconnected callback lifecycle
     * @returns {void}
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        clearInterval(this.interval);
        this.resizeObserver.unobserve(this.ghostGridItemEl);
    }
    /**
     * Slide Left
     * @return {void}
     */
    slideLeft() {
        this.sideScrollTo(this.wrapperEl.scrollLeft - this.containerOuterEl.offsetWidth);
    }
    /**
     * Slide Right
     * @return {void}
     */
    slideRight() {
        this.sideScrollTo(this.wrapperEl.scrollLeft + this.containerOuterEl.offsetWidth);
    }
    /**
     * Check Scroll position to make sure the right nav buttons are visible
     * @return {void}
     */
    checkScroll() {
        if (this.wrapperEl) {
            if (this.wrapperEl.scrollLeft < 5) {
                this.showLeftSliderNav = false;
            }
            else {
                this.showLeftSliderNav = true;
            }
            if (this.wrapperEl.scrollLeft == this.wrapperEl.scrollWidth - this.wrapperEl.offsetWidth) {
                this.showRightSliderNav = false;
            }
            else {
                this.showRightSliderNav = true;
            }
        }
    }
    /**
     * Handle keydown
     * @param {number} targetPosition - where to horizontally scroll
     * @param {boolean} smoothScroll - how to horizontally scroll
     * @return {void}
     */
    sideScrollTo(targetPosition = 0, smoothScroll = true) {
        this.wrapperEl.scrollTo({
            top: 0,
            left: targetPosition,
            behavior: smoothScroll ? 'smooth' : 'instant'
        });
        setTimeout(() => {
            this.checkScroll();
        }, smoothScroll ? 600 : 10); // chrome requires this long for smooth scroll animation
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const classes = {
            [`${this.type}`]: true
        };
        const chevron = html ` <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="17" height="17" fill="currentColor" class="bi bi-chevron-right" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708" /> </svg> `;
        const ghostGrid = this.type == 'slider'
            ? html ` <div class="ghost-grid" aria-hidden="true"> <div class="ghost-grid-item" data-value=${this.gridItemWidth}></div> </div> `
            : '';
        return html ` <section style=${'--ghost-item-width: ' + this.gridItemWidth + 'px'} class="video-list${classMap(classes)}" aria-label=${this.heading ? `${dictionary(this.culture, 'videoList', 'ariaVideoList')} ` + this.heading
            : `${dictionary(this.culture, 'videoList', 'ariaVideoListEmpty')}`}
      >
        <header>
          <h2 id=${ifDefined(this.sliderId ? this.sliderId + '-heading' : undefined)}>
            ${this.heading}
          </h2>
          ${this.showAllLink
            ? html ` <p-button size="medium" color="arrow" href=${this.showAllLink} >${dictionary(this.culture, 'videoList', 'showAll')}</p-button > `
            : ''}
        </header>
        <div class="container-outer">
          ${this.type == 'slider'
            ? html ` <button class="slider-nav-left" aria-controls=${this.sliderId + '-list'} ?disabled=${!this.showLeftSliderNav} aria-label=${dictionary(this.culture, 'videoList', 'slideLeft')} @click=${this.slideLeft} > ${chevron} </button> <button class="slider-nav-right" aria-controls=${this.sliderId + '-list'} ?disabled=${!this.showRightSliderNav} aria-label=${dictionary(this.culture, 'videoList', 'slideRight')} @click=${this.slideRight} > ${chevron} </button> `
            : ''}

          <div class="container">
            <div
              class="wrapper"
              role="list"
              id=${ifDefined(this.sliderId ? this.sliderId + '-list' : undefined)}
              aria-labelledby=${ifDefined(this.sliderId ? this.sliderId + '-heading' : undefined)}
              @wheel=${this.checkScroll}
            >
              ${this.preview && this.videos.length == 0
            ? html ` <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> <p-video-item preview ?dark=${this.dark}></p-video-item> `
            : html ` ${this.liveVideos && this.videos.length > 0 ? this.liveVideos.map((video) => html `
                            <p-video-item
                              culture=${this.culture}
                              heading-tag=${this.heading ? 'h3' : 'h2'}
                              role="listitem"
                              ?is-live=${video.status == 'BROADCASTING'}
                              ?is-upcoming=${video.status == 'PENDING'}
                              ?ended=${video.status == 'FINISHED'}
                              link=${this.liveBaseUrl + '/' + video.id}
                              video-id=${video.id}
                              summary=${video.description}
                              heading=${video.title}
                              poster=${video.poster}
                              duration=${video.length}
                              date=${video.liveAt}
                            >
                            </p-video-item>
                          `)
                : ''}
                    ${this.videos && this.videos.length > 0
                ? this.videos.map((video) => html ` <p-video-item culture=${this.culture} heading-tag=${this.heading ? 'h3' : 'h2'} role="listitem" link=${this.videoBaseUrl + '/' + video.id} video-id=${video.id} summary=${video.description} heading=${video.name} poster=${video.poster} duration=${this.showRuntime ? video.length : ''} > </p-video-item> `)
                : ''}
                  `}
            </div>
          </div>
        </div>
        ${ghostGrid}
      </section>
    `;
    }
};
__decorate([
    property({ type: String, reflect: true })
], PVideoList.prototype, "type", void 0);
__decorate([
    property({ type: String, reflect: true })
], PVideoList.prototype, "heading", void 0);
__decorate([
    property({ type: String, reflect: true })
], PVideoList.prototype, "sort", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'sort-order' })
], PVideoList.prototype, "sortOrder", void 0);
__decorate([
    property({ type: Number, reflect: true })
], PVideoList.prototype, "limit", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PVideoList.prototype, "preview", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PVideoList.prototype, "dark", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'show-all-link' })
], PVideoList.prototype, "showAllLink", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'video-base-url' })
], PVideoList.prototype, "videoBaseUrl", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'live-base-url' })
], PVideoList.prototype, "liveBaseUrl", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'slider-id' })
], PVideoList.prototype, "sliderId", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'folder-id' })
], PVideoList.prototype, "folderId", void 0);
__decorate([
    property({ type: String })
], PVideoList.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-runtime' })
], PVideoList.prototype, "showRuntime", void 0);
__decorate([
    property()
], PVideoList.prototype, "videos", void 0);
__decorate([
    property()
], PVideoList.prototype, "liveVideos", void 0);
__decorate([
    state()
], PVideoList.prototype, "showLeftSliderNav", void 0);
__decorate([
    state()
], PVideoList.prototype, "showRightSliderNav", void 0);
__decorate([
    state()
], PVideoList.prototype, "gridItemWidthFirstCheck", void 0);
__decorate([
    state()
], PVideoList.prototype, "gridItemWidth", void 0);
__decorate([
    query('.wrapper')
], PVideoList.prototype, "wrapperEl", void 0);
__decorate([
    query('.container')
], PVideoList.prototype, "containerEl", void 0);
__decorate([
    query('.container-outer')
], PVideoList.prototype, "containerOuterEl", void 0);
__decorate([
    query('.ghost-grid-item')
], PVideoList.prototype, "ghostGridItemEl", void 0);
PVideoList = __decorate([
    customElement('p-video-list')
    /**
     * PVideoList component
     *
     * @class PVideoList
     * @typedef {PVideoList}
     * @extends {LitElement}
     */
], PVideoList);
export { PVideoList };
